import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  User as FirebaseUser 
} from 'firebase/auth';
import { auth, firestore, FIRESTORE_COLECOES } from '../firebase';
import { buscarPerfilCliente, salvarPerfilCliente, escutarPerfilCliente } from '../services/clientService';
import type { UsuarioFirestore } from '../types/firestore';
import { doc, getDoc } from 'firebase/firestore';

const LOCAL_SESSION_KEY = 'abh_logged_client_uid';

interface AuthContextType {
  user: { uid: string; email?: string | null } | null;
  perfil: UsuarioFirestore | null;
  isOperator: boolean;
  loading: boolean;
  login: (identificador: string, pass: string) => Promise<void>;
  cadastro: (emailOrPhone: string, pass: string, dadosPerfil: Partial<UsuarioFirestore>) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function formatToEmail(identificador: string): string {
  const clean = identificador.trim().toLowerCase();
  if (clean.includes('@')) {
    return clean;
  }
  const digitsOnly = clean.replace(/\D/g, '');
  return `cli_${digitsOnly || 'user'}@abhmotoboys.com`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<{ uid: string; email?: string | null } | null>(null);
  const [perfil, setPerfil] = useState<UsuarioFirestore | null>(null);
  const [isOperator, setIsOperator] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribePerfil: (() => void) | null = null;

    const checkOperatorStatus = async (uid: string) => {
      try {
        const docSnap = await getDoc(doc(firestore, 'operadores', uid));
        setIsOperator(docSnap.exists());
      } catch (e) {
        setIsOperator(false);
      }
    };

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser({ uid: currentUser.uid, email: currentUser.email });
        localStorage.setItem(LOCAL_SESSION_KEY, currentUser.uid);
        await checkOperatorStatus(currentUser.uid);
        unsubscribePerfil = escutarPerfilCliente(currentUser.uid, (p) => {
          setPerfil(p);
          setLoading(false);
        });
      } else {
        // Checar se existe sessão salva em localStorage (Fallback para ambiente sem Auth ativado no console)
        const savedUid = localStorage.getItem(LOCAL_SESSION_KEY);
        if (savedUid) {
          setUser({ uid: savedUid });
          await checkOperatorStatus(savedUid);
          unsubscribePerfil = escutarPerfilCliente(savedUid, (p) => {
            setPerfil(p);
            setLoading(false);
          });
        } else {
          if (unsubscribePerfil) unsubscribePerfil();
          setUser(null);
          setPerfil(null);
          setIsOperator(false);
          setLoading(false);
        }
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribePerfil) unsubscribePerfil();
    };
  }, []);

  const login = async (identificador: string, pass: string) => {
    const emailFormat = formatToEmail(identificador);
    try {
      // Tentativa 1: Firebase Auth Oficial
      const cred = await signInWithEmailAndPassword(auth, emailFormat, pass);
      setUser({ uid: cred.user.uid, email: cred.user.email });
      localStorage.setItem(LOCAL_SESSION_KEY, cred.user.uid);
    } catch (error: any) {
      // Se Firebase Auth não estiver habilitado no console ou retornar 400 operation-not-allowed
      if (
        error.code === 'auth/operation-not-allowed' || 
        error.code === 'auth/admin-restricted-operation' ||
        error.message?.includes('400')
      ) {
        // Fallback de Sessão via Firestore
        const fallbackUid = 'user_' + btoa(emailFormat).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
        const p = await buscarPerfilCliente(fallbackUid);
        if (p) {
          setUser({ uid: fallbackUid, email: emailFormat });
          setPerfil(p);
          localStorage.setItem(LOCAL_SESSION_KEY, fallbackUid);
          return;
        }
      }
      throw error;
    }
  };

  const cadastro = async (emailOrPhone: string, pass: string, dadosPerfil: Partial<UsuarioFirestore>) => {
    const emailFormat = formatToEmail(emailOrPhone);
    try {
      // Tentativa 1: Firebase Auth Oficial
      const cred = await createUserWithEmailAndPassword(auth, emailFormat, pass);
      const uid = cred.user.uid;

      await salvarPerfilCliente(uid, {
        uid,
        nome: dadosPerfil.nome || 'Cliente ABH',
        empresa: dadosPerfil.empresa || '',
        whatsapp: dadosPerfil.whatsapp || emailOrPhone,
        cnpj_cpf: dadosPerfil.cnpj_cpf || ''
      });

      setUser({ uid, email: cred.user.email });
      localStorage.setItem(LOCAL_SESSION_KEY, uid);
    } catch (error: any) {
      // Fallback gracioso se Firebase Auth estiver desativado no console do projeto
      if (
        error.code === 'auth/operation-not-allowed' || 
        error.code === 'auth/admin-restricted-operation' ||
        error.message?.includes('400')
      ) {
        const fallbackUid = 'user_' + btoa(emailFormat).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
        
        await salvarPerfilCliente(fallbackUid, {
          uid: fallbackUid,
          nome: dadosPerfil.nome || 'Cliente ABH',
          empresa: dadosPerfil.empresa || '',
          whatsapp: dadosPerfil.whatsapp || emailOrPhone,
          cnpj_cpf: dadosPerfil.cnpj_cpf || ''
        });

        setUser({ uid: fallbackUid, email: emailFormat });
        localStorage.setItem(LOCAL_SESSION_KEY, fallbackUid);
        return;
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (_e) {
      // Ignore
    }
    localStorage.removeItem(LOCAL_SESSION_KEY);
    setUser(null);
    setPerfil(null);
  };

  return (
    <AuthContext.Provider value={{ user, perfil, isOperator, loading, login, cadastro, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}
